// OWNER: AREA-TEMPLATES (templates/*, partials/*, assets/*). Components go in src/features/assets.
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/PageHeader'
import { Select } from '@/components/ui/select'
import { AssetsTable } from '@/features/assets/AssetsTable'
import { ownerFilters, type OwnerView } from '@/features/templates/api'
import { useAppScope } from '@/shell/useAppScope'

interface AssetsSearch {
  q?: string
  owner?: OwnerView
}

export const Route = createFileRoute('/assets/')({
  validateSearch: (s: Record<string, unknown>): AssetsSearch => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    owner: s.owner === 'app' || s.owner === 'shared' ? s.owner : undefined,
  }),
  component: AssetsPage,
})

function AssetsPage() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { appId, application } = useAppScope()
  const view: OwnerView = search.owner === 'app' && !appId ? 'all' : (search.owner ?? 'all')
  const f = ownerFilters(view, appId)
  const ownerOptions = [
    { value: 'all' as const, label: application ? `${application.name} and shared` : 'All owners' },
    ...(appId
      ? [{ value: 'app' as const, label: `${application?.name ?? 'This application'} only` }]
      : []),
    { value: 'shared' as const, label: 'Shared library' },
  ]
  return (
    <>
      <PageHeader
        title="Assets"
        description="Reusable files, versioned like templates: logos for inline images, terms and conditions to attach, and images for PDF documents. An application’s asset overrides a shared asset with the same key."
      />
      <AssetsTable
        filters={{ app: f.app, includeShared: f.includeShared }}
        q={search.q ?? ''}
        onQChange={(q) =>
          void navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true })
        }
        showOwner
        defaultOwner={appId}
        persistKey="assets"
        toolbar={
          <Select<OwnerView>
            size="sm"
            className="w-full sm:w-56"
            aria-label="Owner"
            value={view}
            onValueChange={(v) =>
              void navigate({
                search: (prev) => ({ ...prev, owner: v === 'all' ? undefined : v }),
                replace: true,
              })
            }
            options={ownerOptions}
          />
        }
      />
    </>
  )
}
